import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { IconWhatsApp } from './Icons';
import { API_BASE_URL } from '../../config';
import toast from 'react-hot-toast';

interface WhatsAppSendButtonProps {
    /** The text content to send to WhatsApp */
    content: string;
    /** Label shown in the WhatsApp message header, e.g. "Macro Report" */
    label?: string;
    /** Optional extra CSS classes */
    className?: string;
    /** Button style variant */
    variant?: 'default' | 'ghost';
}

/**
 * Sends a text report/summary to the authenticated user's linked WhatsApp number
 * via the QuantClaw bot. User must have a phone number set in their profile.
 */
const WhatsAppSendButton: React.FC<WhatsAppSendButtonProps> = ({
    content,
    label = 'Report',
    className = '',
    variant = 'default',
}) => {
    const { currentUser } = useAuth();
    const [isSending, setIsSending] = useState(false);

    const handleSend = async () => {
        if (!currentUser || !content.trim() || isSending) return;
        setIsSending(true);

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/whatsapp/push/content`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ content, label }),
            });

            const json = await res.json();

            if (res.ok) {
                toast.success('Sent to your WhatsApp');
            } else if (json.error === 'no_phone') {
                toast.error('No phone linked. Add your WhatsApp number in Profile Settings.', { duration: 5000 });
            } else {
                toast.error(json.message || json.error || 'Failed to send');
            }
        } catch {
            toast.error('Network error — could not send to WhatsApp');
        } finally {
            setIsSending(false);
        }
    };

    const baseStyle = variant === 'ghost'
        ? 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 border border-transparent hover:border-emerald-200 transition-colors'
        : 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors';

    return (
        <button
            onClick={handleSend}
            disabled={isSending || !content.trim()}
            className={`${baseStyle} disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
            title="Send to WhatsApp"
        >
            {isSending
                ? <span className="w-3.5 h-3.5 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin" />
                : <IconWhatsApp size={13} />
            }
            {isSending ? 'Sending…' : 'Send to WhatsApp'}
        </button>
    );
};

export default WhatsAppSendButton;
